import React, { useState, useEffect } from 'react';
import ReactPaginate from 'react-paginate';
import * as XLSX from 'xlsx/xlsx.mjs';
import { RiFileExcel2Line } from "react-icons/ri";
import { FiAlertTriangle } from 'react-icons/fi';

export const TableTotals = ({
    columns,
    data,
    msg,
    function_,
    placeholderSearch = "Introduce Nombre / Descripción",
    showPagination = true,
    tableHeight = "700px",
    showControls = true,
    showTotals = false,
    countRow = false, // nueva prop para contador de filas
    showAverage = false,
}) => {

    const [currentPage, setCurrentPage] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [sortConfig, setSortConfig] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [columnOrder, setColumnOrder] = useState(columns);

    useEffect(() => {
        setColumnOrder(columns);
    }, [columns]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = data ? [...data].sort((a, b) => {
        if (!sortConfig) return 0;
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (!isNaN(aValue) && !isNaN(bValue)) {
            return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        return sortConfig.direction === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
    }) : [];

    const filteredData = sortedData.filter(item =>
        Object.values(item).some(value =>
            value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const offset = currentPage * perPage;
    const currentPageItems = filteredData.slice(offset, offset + perPage);

    const handlePageChange = (selectedPage) => setCurrentPage(selectedPage.selected);
    const handlePerPageChange = (e) => {
        setPerPage(Number(e.target.value));
        setCurrentPage(0);
    };

    const exportToXLS = () => {
        const d = new Date();
        const timestamp = d.toISOString().replace(/[-:]/g, '').replace('T', '_').slice(0, 15);
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, `Export_${timestamp}.xlsx`);
    };

    // Drag and Drop
    const handleDragStart = (e, index) => {
        e.dataTransfer.setData('draggedIndex', index);
    };

    const handleDrop = (e, dropIndex) => {
        const draggedIndex = parseInt(e.dataTransfer.getData('draggedIndex'), 10);
        if (isNaN(draggedIndex)) return;

        const updatedColumns = [...columnOrder];
        const [draggedColumn] = updatedColumns.splice(draggedIndex, 1);
        updatedColumns.splice(dropIndex, 0, draggedColumn);

        setColumnOrder(updatedColumns);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    // Totales personalizados
    const totalRow = {};
    if (showTotals) {
        columns.forEach(col => {
            const key = col.dataIndex;
            if (col.isTotal && key) {
                totalRow[key] = data.reduce((acc, item) => acc + parseFloat(item[key] || 0), 0);
            } else {
                totalRow[key] = '';
            }
        });
    }

    const averageRow = {};
    if (showAverage) {
        columns.forEach(col => {
            const key = col.dataIndex;
            if (col.isTotal && key) { // solo columnas marcadas con isTotal
                const numericValues = data.map(item => parseFloat(item[key] || 0));
                const sum = numericValues.reduce((acc, val) => acc + val, 0);
                averageRow[key] = numericValues.length > 0 ? sum / numericValues.length : 0;
            } else {
                averageRow[key] = '';
            }
        });
    }

    // Columnas a mostrar incluyendo contador de filas
    let displayColumns = [...columnOrder];
    if (countRow) {
        displayColumns = [
            {
                header: '#',
                dataIndex: '__index__',
                classHeader: 'text-center',
                classRow: 'text-center',
                width: '20px', // 👈 define el ancho
            },
            ...displayColumns
        ];
    }


    const formatValue = (value, col) => {
        if (value === null || value === undefined || value === '') {
            return value;
        }

        if (typeof value === 'number' && col.decimals !== undefined) {
            return value.toFixed(col.decimals);
        }

        return value;
    };

    return (
        <>
            {sortedData.length > 0 ? (
                <>
                    {/* Top controls */}
                    {showControls && (
                        <div className="row mb-3">
                            <div className="col-6">
                                <label className="form-label">Buscar:</label>
                                <input
                                    type="search"
                                    className="form-control"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    autoComplete="off"
                                    placeholder={placeholderSearch}
                                />
                            </div>

                            <div className="col-3">
                                <label className="form-label">Mostrar:</label>
                                <select value={perPage} onChange={handlePerPageChange} className="form-select">
                                    {[20, 30, 40, 50, 60, 70, 80, 90, 100, 200].map(size => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-3 text-end align-self-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-success fw-bold"
                                    onClick={exportToXLS}
                                >
                                    <RiFileExcel2Line size={25} /> Exportar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Tabla */}
                    <div className="table-responsive mt-2 mb-4" style={{ maxHeight: tableHeight, overflowY: 'auto', overflowX: "auto", }}>
                        <table className="table table-sm table-striped table-hover mb-0">
                            <thead>
                                <tr className="th-sticky">
                                    {displayColumns.map((col, index) => (
                                        <th
                                            key={index}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onClick={() => requestSort(col.dataIndex)}
                                            className={`sticky-header bg-white ${col.classHeader || ''}`}
                                            style={{
                                                cursor: 'move',
                                                userSelect: 'none',
                                                width: col.width || 'auto',        // ✅ aplica ancho
                                                maxWidth: col.width || 'auto',     // opcional para restringir
                                            }}
                                        >
                                            {col.header}
                                            {sortConfig?.key === col.dataIndex && (
                                                <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {currentPageItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={displayColumns.length}>No se encontraron resultados.</td>
                                    </tr>
                                ) : (
                                    currentPageItems.map((row, idx) => (
                                        <tr key={idx} onDoubleClick={() => function_(row)} style={{ cursor: 'pointer' }}>
                                            {displayColumns.map((col, i) => (
                                                <td
                                                    key={i}
                                                    className={col.classRow}
                                                    style={{
                                                        width: col.width || 'auto',
                                                        maxWidth: col.width || 'auto',
                                                        textAlign: col.classRow?.includes('text-end') ? 'right' : 'center', // opcional
                                                    }}
                                                >
                                                    {col.dataIndex === '__index__'
                                                        ? offset + idx + 1
                                                        : col.renderer
                                                            ? col.renderer(row[col.dataIndex], row)
                                                            : row[col.dataIndex]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                                {showTotals && currentPageItems.length > 0 && (
                                    <tr className="table-secondary fw-bold">
                                        {displayColumns.map((col, i) => {
                                            if (i === 0) return <td key={i}>Total</td>;

                                            // Evitar renderizar botones o acciones
                                            if (col.isAction) return <td key={i}></td>;

                                            return (
                                                <td key={i} className={col.classRow}>
                                                    {col.renderer && !col.isAction
                                                        ? col.renderer(totalRow[col.dataIndex], totalRow)
                                                        : formatValue(totalRow[col.dataIndex], col)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                )}

                                {showAverage && currentPageItems.length > 0 && (
                                    <tr className="table-info fw-bold">
                                        {displayColumns.map((col, i) => {
                                            if (i === 0) return <td key={i}>Promedio (%)</td>;

                                            // Evitar renderizar botones o acciones
                                            if (col.isAction) return <td key={i}></td>;

                                            return (
                                                <td key={i} className={col.classRow}>
                                                    {col.renderer && !col.isAction
                                                        ? col.renderer(averageRow[col.dataIndex], averageRow)
                                                        : formatValue(averageRow[col.dataIndex], col)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    {showPagination && (
                        <ReactPaginate
                            containerClassName="pagination pagination-sm"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            activeClassName="active"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            previousLabel="<"
                            nextLabel=">"
                            pageCount={Math.ceil(filteredData.length / perPage)}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            ) : (
                <>
                    {msg ? (
                        <div className="alert alert-light border-dark text-dark fw-bold mt-3 mb-5" role="alert">
                            <FiAlertTriangle size="18" /> {msg}
                        </div>
                    ) : (
                        <div className="alert alert-light fw-bold border-dark text-dark mt-3 mb-5" role="alert">
                            <FiAlertTriangle size="18" /> No hay datos por mostrar
                        </div>
                    )}
                </>
            )}
        </>
    );
};
